import { HelixProvider } from '@helix-ds/react'
import React from 'react'
import { Provider } from 'react-redux'
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
// Import auth components
import { Auth0Provider, type AppState } from '@auth0/auth0-react'
import { ApplicationFooter } from './components/common/ApplicationFooter'
import CompletedRequestForm from './components/CompletedRequestForm'
import OrderPlacedConfirmation from './components/inProgressOrder/OrderPlacedConfirmation'
// import MockOrderForm from './components/orderForm/MockOderFormInfo'
// import SecureRoute from './features/auth/components/SecureRoute'
import CaseOnlyRedirectGuard from './components/CaseOnlyRedirectGuard'
import OrderingSessionGuard from './components/OrderingSessionGuard'
import OrderScopeGuard from './components/OrderScopeGuard'
import SecureEHRRoute from './components/SecureEHRRoute'
import { UiConfigProvider } from './config/UiConfigProvider'
import auth0Config from './features/auth/auth0/config/auth0'
import { EpicSandboxCallback } from './features/auth/components/Callback'
import FirstTimeWelcome from './features/auth/components/FirstTimeWelcome'
import Launch from './features/auth/components/Launch'
import StandaloneAuthGuard from './features/auth/components/StandaloneAuthGuard'
import StandaloneCallback from './features/auth/components/StandaloneCallback'
import StandaloneLogin from './features/auth/components/StandaloneLogin'
import { AuthorizationProvider, RequireOrgAdmin } from './features/authorization'
import { isTrfSigningPath } from './features/docusign/signingPath'
import { useSentryUser } from './hooks/useSentryUser'
import Admin from './pages/admin/Admin'
import { CasesListRoute } from './pages/cases/CasesList'
import { HealthCheck } from './pages/HealthCheck'
import AcceptInvitation from './pages/invitations/AcceptInvitation'
import NoAccess from './pages/noAccess/NoAccess'
import { CaseByIdRoute, CaseConnectRoute } from './pages/order/CaseConnect'
import { OrderGuidance } from './pages/order/OrderGuidance'
import { OrderInProgressRoute } from './pages/order/OrderInProgress'
import { OrderReadyToSend } from './pages/order/OrderReadyToSend'
import { OrderResultReady } from './pages/order/OrderResultReady'
import { OrderStart } from './pages/order/OrderStart'
import Orders from './pages/orders/Orders'
import { SignLegacyEntry } from './pages/sign/SignLegacyEntry'
import { SignReturn } from './pages/sign/SignReturn'
import { SignTrf } from './pages/sign/SignTrf'
import { store } from './store'
import { isInternalReturnTo } from './utils/safeReturnTo'

/**
 * Local-only editor harness. The `lazy` call is built behind the same `DEV` check as its
 * route: at module scope the dynamic import is a real edge in the graph, so the bundler
 * emits the chunk and links it from the entry even though nothing can ever navigate there.
 */
const ClinicalSummaryEditorHarness = import.meta.env.DEV
  ? React.lazy(() =>
      import('./pages/dev/ClinicalSummaryEditorHarness').then((m) => ({
        default: m.ClinicalSummaryEditorHarness,
      }))
    )
  : null

function OrderWorkspaceLayout() {
  return (
    <div className="flex min-h-screen flex-col bg-background-variant-gray dark:bg-gray-950">
      <div className="flex min-h-0 flex-1 flex-col">
        <Outlet />
      </div>
      <ApplicationFooter className="shrink-0 bg-background-variant-gray dark:bg-gray-950" />
    </div>
  )
}

/**
 * Inner component that uses the Sentry user tracking hook
 * Must be inside Auth0Provider to access auth state
 */
const AppRoutes: React.FC = () => {
  // Sync Auth0 user state with Sentry for anonymous error tracking
  useSentryUser()

  return (
    <Routes>
      {/* Public routes - Disabled*/}
      {/* <Route
        path="/mock"
        element={
          <SecureRoute>
            <MockOrderForm />
          </SecureRoute>
        }
      /> */}
      {/* Epic Flow Routes */}
      <Route path="/launch" element={<Launch />} />
      <Route path="/epic-sandbox/callback" element={<EpicSandboxCallback />} />
      <Route path="/order" element={<OrderWorkspaceLayout />}>
        <Route
          path="start"
          element={
            <SecureEHRRoute>
              <OrderingSessionGuard>
                <OrderStart />
              </OrderingSessionGuard>
            </SecureEHRRoute>
          }
        />
        <Route
          path="ready"
          element={
            <SecureEHRRoute>
              <OrderingSessionGuard>
                <OrderReadyToSend />
              </OrderingSessionGuard>
            </SecureEHRRoute>
          }
        />
        <Route
          path="in-progress/:orderId?/:patientId?"
          element={
            // No OrderScopeGuard: OrderInProgress's GET /database/orders/{id} is
            // org-scoped server-side (404 → shared "Order not found"), so a
            // separate scope probe would only add a serial request + loader.
            <SecureEHRRoute>
              <OrderInProgressRoute />
            </SecureEHRRoute>
          }
        />
        <Route
          path="results/:orderId"
          element={
            <SecureEHRRoute>
              <OrderScopeGuard>
                <OrderResultReady />
              </OrderScopeGuard>
            </SecureEHRRoute>
          }
        />
        <Route
          path="guidance/:orderId"
          element={
            <SecureEHRRoute>
              <OrderScopeGuard>
                <OrderGuidance />
              </OrderScopeGuard>
            </SecureEHRRoute>
          }
        />
        <Route
          path="case-connect/:orderId"
          element={
            <SecureEHRRoute>
              <OrderScopeGuard>
                <CaseConnectRoute />
              </OrderScopeGuard>
            </SecureEHRRoute>
          }
        />
      </Route>
      {/* Pilot order wizard removed; send old links to canonical-first start. */}
      <Route path="/order/wizard-v2" element={<Navigate to="/order/start" replace />} />
      {/* /form was the legacy TRF wizard — replaced by /order/start (canonical-first flow). */}
      <Route path="/form" element={<Navigate to="/order/start" replace />} />
      <Route
        path="/in-progress-order/:orderId?/:patientId?"
        element={
          <SecureEHRRoute>
            <OrderScopeGuard>
              <OrderPlacedConfirmation />
            </OrderScopeGuard>
          </SecureEHRRoute>
        }
      />
      <Route
        path="/form/complete"
        element={
          <SecureEHRRoute>
            <CompletedRequestForm />
          </SecureEHRRoute>
        }
      />
      {/* Standalone Auth0 Flow Routes */}
      <Route path="/login" element={<StandaloneLogin />} />
      <Route path="/login/callback" element={<StandaloneCallback />} />
      <Route
        path="/welcome"
        element={
          <StandaloneAuthGuard>
            <CaseOnlyRedirectGuard>
              <FirstTimeWelcome />
            </CaseOnlyRedirectGuard>
          </StandaloneAuthGuard>
        }
      />
      <Route
        path="/orders"
        element={
          <StandaloneAuthGuard>
            <CaseOnlyRedirectGuard>
              <Orders />
            </CaseOnlyRedirectGuard>
          </StandaloneAuthGuard>
        }
      />
      {/* Clinician Connect landing (case list) — case:view gated; org-optional
          (org-less participants reach it too). No OrderScopeGuard. */}
      <Route
        path="/cases"
        element={
          <StandaloneAuthGuard>
            <CasesListRoute />
          </StandaloneAuthGuard>
        }
      />
      {/* Open a case by CASE id (org-less entry). case:view gated; per-case
          authz is still enforced server-side. Deliberately NO OrderScopeGuard
          (its probe 401s org-less users). */}
      <Route
        path="/cases/:caseId"
        element={
          <StandaloneAuthGuard>
            <CaseByIdRoute />
          </StandaloneAuthGuard>
        }
      />
      {/* Friendly landing for an authenticated user with no usable access yet
          (e.g. a pending/unaccepted invite). Any authenticated user may see it;
          it exposes nothing org/case-specific. */}
      <Route
        path="/no-access"
        element={
          <StandaloneAuthGuard>
            <NoAccess />
          </StandaloneAuthGuard>
        }
      />
      <Route
        path="/admin"
        element={
          <StandaloneAuthGuard>
            <CaseOnlyRedirectGuard>
              <RequireOrgAdmin>
                <Admin />
              </RequireOrgAdmin>
            </CaseOnlyRedirectGuard>
          </StandaloneAuthGuard>
        }
      />
      <Route
        path="/invitations/accept/:token"
        element={
          <StandaloneAuthGuard>
            <AcceptInvitation />
          </StandaloneAuthGuard>
        }
      />
      {/* Health check (Helix smoke: Button + HelixProvider) */}
      <Route path="/health" element={<HealthCheck />} />
      {import.meta.env.DEV && ClinicalSummaryEditorHarness ? (
        <Route
          path="/dev/clinical-summary-editor"
          element={
            <React.Suspense fallback={null}>
              <ClinicalSummaryEditorHarness />
            </React.Suspense>
          }
        />
      ) : null}
      {/* Embedded TRF signing. Guarded like the order pages, so the emailed
          signing link takes a signed-out clinician through /login and back here
          via returnTo. /sign* carries the route-scoped CSP/COEP that lets the
          DocuSign ceremony frame load (see spa-hosting.ts). */}
      <Route
        path="/sign/:orderId"
        element={
          <SecureEHRRoute>
            <SignTrf />
          </SecureEHRRoute>
        }
      />
      {/* Links from before the order id moved into the path. Never signs
          anyone in: a leftover reminder token is discarded unread. */}
      <Route path="/sign" element={<SignLegacyEntry />} />
      {/* DocuSign's returnUrl target is NOT routed here. It is rendered by
          `App` before any provider mounts — see SIGNING_RETURN_PATH — because a
          provider instantiated inside the ceremony iframe is a second copy of
          this app running three frames deep, and Auth0's is destructive there.
          Kept out of this table so a route added later cannot shadow it. */}
      {/* Default route */}
      <Route path="/" element={<Navigate to="/login" replace />} />
    </Routes>
  )
}

/**
 * The main App component, which sets up routing and the overall application structure.
 * It fetches all page configurations on startup and dynamically generates routes.
 */
/**
 * DocuSign's configured return URL. Reached only as a full document load inside
 * the ceremony iframe, when the signing session ends.
 */
const SIGNING_RETURN_PATH = '/sign/return'

const App: React.FC = () => {
  const navigate = useNavigate()
  const { pathname } = useLocation()

  // After the Auth0 redirect round-trip, honor a ``returnTo`` deep link captured
  // at login time (e.g. an /order/results/:id link from a results email). When
  // absent, leave navigation to StandaloneCallback's first-login routing.
  const onRedirectCallback = (appState?: AppState) => {
    const returnTo = (appState as { returnTo?: string } | undefined)?.returnTo
    // ``returnTo`` is round-tripped through Auth0 appState (outside our trust
    // boundary), so only honor internal absolute paths (see isInternalReturnTo /
    // OWASP Unvalidated Redirects and Forwards Cheat Sheet).
    if (isInternalReturnTo(returnTo)) {
      // The callback document was served without the /sign* headers the
      // ceremony frame needs, so the signing page gets a document of its own.
      if (isTrfSigningPath(returnTo)) window.location.replace(returnTo)
      else navigate(returnTo, { replace: true })
    }
  }

  // The ceremony's return frame renders alone, above every provider.
  //
  // It is a fresh document inside the DocuSign iframe, so each provider mounted
  // here is a second copy of that machinery running three frames deep. Auth0's
  // is the one that does harm: silent authentication cannot complete from there
  // and comes back `login_required`, which auth0-spa-js answers by clearing the
  // token cache — and that cache is `localStorage`, shared with the window the
  // clinician is signed into. The frame would log them out of their own session
  // while telling them their signature was received.
  //
  // Nothing it renders needs a provider. It reads DocuSign's query string, hands
  // the outcome to the page that frames it, and offers a link.
  //
  // Trailing slashes are ignored here as they are by the router, or
  // `/sign/:orderId` would take `/sign/return/` and mount Auth0 in the frame.
  if (pathname.replace(/\/+$/, '') === SIGNING_RETURN_PATH) {
    return <SignReturn />
  }

  return (
    <Provider store={store}>
      <Auth0Provider {...auth0Config} onRedirectCallback={onRedirectCallback}>
        <HelixProvider>
          <AuthorizationProvider>
            <UiConfigProvider>
              <AppRoutes />
            </UiConfigProvider>
          </AuthorizationProvider>
        </HelixProvider>
      </Auth0Provider>
    </Provider>
  )
}

export default App
